import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

// ============================================================
// Types
// ============================================================

export interface Permission {
  id: string;
  label: string;
}

export interface PermissionsSelectorProps {
  availablePermissions: Permission[];
  grantedPermissions: string[];
  onGrantedPermissionsChange: (permissions: string[]) => void;
}

// ============================================================
// Icon Components
// ============================================================

const CheckIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// PermissionsSelector Component
// ============================================================

export default function PermissionsSelector({
  availablePermissions,
  grantedPermissions,
  onGrantedPermissionsChange,
}: PermissionsSelectorProps) {
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const isSmallScreen = width < 768;

  const [availableSearch, setAvailableSearch] = useState('');
  const [grantedSearch, setGrantedSearch] = useState('');

  // Theme colors
  const themeColors = {
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    listBackground: isDark ? colors.backgroundAlt : '#F8FAFC',
    border: colors.border,
    inputBackground: colors.inputBackground,
    grantedItemBackground: isDark ? 'rgba(59, 130, 246, 0.2)' : '#EFF6FF',
    checkboxBackground: colors.inputBackground,
  };

  // Filter permissions by search
  const filteredAvailable = availablePermissions
    .filter(p => !grantedPermissions.includes(p.id))
    .filter(p => p.label.includes(availableSearch));

  const filteredGranted = availablePermissions
    .filter(p => grantedPermissions.includes(p.id))
    .filter(p => p.label.includes(grantedSearch));

  // Toggle single permission
  const togglePermission = (permissionId: string) => {
    if (grantedPermissions.includes(permissionId)) {
      onGrantedPermissionsChange(grantedPermissions.filter(p => p !== permissionId));
    } else {
      onGrantedPermissionsChange([...grantedPermissions, permissionId]);
    }
  };

  // Add all permissions
  const addAllPermissions = () => {
    onGrantedPermissionsChange(availablePermissions.map(p => p.id));
  };

  // Remove all permissions
  const removeAllPermissions = () => {
    onGrantedPermissionsChange([]);
  };

  return (
    <View>
      <Text style={[styles.permissionsTitle, { color: themeColors.textPrimary }]}>{t('permissions.usagePermissions')}</Text>

      <View style={[styles.permissionsContainer, isSmallScreen && styles.permissionsContainerSmall]}>
        {/* Available Permissions */}
        <View style={[styles.permissionsList, { backgroundColor: themeColors.listBackground, borderColor: themeColors.border }]}>
          <Text style={[styles.permissionsListTitle, { color: themeColors.textPrimary }]}>{t('permissions.availablePermissions')}</Text>
          <Text style={[styles.permissionsListHint, { color: themeColors.textMuted }]}>{t('permissions.selectToChooseHint')}</Text>

          <View style={styles.searchBox}>
            <TextInput
              style={[styles.searchInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              placeholder={t('permissions.searchPermissions')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
              value={availableSearch}
              onChangeText={setAvailableSearch}
            />
            <Text style={[styles.searchLabel, { color: themeColors.textMuted }]}>{t('common.search')}</Text>
          </View>

          <ScrollView style={styles.permissionsScroll}>
            {filteredAvailable.map((permission) => (
              <TouchableOpacity
                key={permission.id}
                style={styles.permissionItem}
                onPress={() => togglePermission(permission.id)}
              >
                <View style={[styles.checkbox, { backgroundColor: themeColors.checkboxBackground, borderColor: themeColors.border }]} />
                <Text style={[styles.permissionLabel, { color: themeColors.textPrimary }]}>{permission.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity style={styles.addAllBtn} onPress={addAllPermissions}>
            <Text style={styles.addAllText}>{t('permissions.addAll')} &raquo;</Text>
          </TouchableOpacity>
        </View>

        {/* Granted Permissions */}
        <View style={[styles.permissionsList, { backgroundColor: themeColors.listBackground, borderColor: themeColors.border }]}>
          <Text style={[styles.permissionsListTitle, { color: themeColors.textPrimary }]}>{t('permissions.grantedPermissions')}</Text>
          <Text style={[styles.permissionsListHint, { color: themeColors.textMuted }]}>{t('permissions.selectToRemoveHint')}</Text>

          <View style={styles.searchBox}>
            <TextInput
              style={[styles.searchInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              placeholder={t('permissions.searchPermissions')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
              value={grantedSearch}
              onChangeText={setGrantedSearch}
            />
            <Text style={[styles.searchLabel, { color: themeColors.textMuted }]}>{t('common.search')}</Text>
          </View>

          <ScrollView style={styles.permissionsScroll}>
            {filteredGranted.map((permission) => (
              <TouchableOpacity
                key={permission.id}
                style={[styles.permissionItem, { backgroundColor: themeColors.grantedItemBackground }]}
                onPress={() => togglePermission(permission.id)}
              >
                <View style={[styles.checkbox, styles.checkboxChecked]}>
                  <CheckIcon size={12} color="#FFFFFF" />
                </View>
                <Text style={[styles.permissionLabel, { color: themeColors.textPrimary }]}>{permission.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity style={styles.removeAllBtn} onPress={removeAllPermissions}>
            <Text style={styles.removeAllText}>&laquo; {t('permissions.removeAll')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  permissionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  permissionsContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  permissionsContainerSmall: {
    flexDirection: 'column',
  },
  permissionsList: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
  },
  permissionsListTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'right',
    marginBottom: 4,
  },
  permissionsListHint: {
    fontSize: 11,
    color: '#64748B',
    textAlign: 'right',
    marginBottom: 12,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    height: 36,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 13,
    color: '#414042',
    backgroundColor: '#FFFFFF',
    textAlign: 'right',
  },
  searchLabel: {
    fontSize: 13,
    color: '#64748B',
  },
  permissionsScroll: {
    maxHeight: 150,
    marginBottom: 12,
  },
  permissionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 8,
    paddingHorizontal: 8,
    gap: 8,
    borderRadius: 4,
  },
  permissionItemGranted: {
    backgroundColor: '#EFF6FF',
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  permissionLabel: {
    fontSize: 13,
    color: '#414042',
    textAlign: 'right',
  },
  addAllBtn: {
    alignSelf: 'flex-end',
    paddingVertical: 4,
  },
  addAllText: {
    fontSize: 13,
    color: '#3B82F6',
  },
  removeAllBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 4,
  },
  removeAllText: {
    fontSize: 13,
    color: '#EF4444',
  },
});
